import { useQuery } from '@tanstack/react-query'
import { consoleQueries, tenantQueries } from '@milliswift/api-client'
import {
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  ChartCard,
  AreaTrendChart,
  Meter,
  PageSkeleton,
  PreviewDataBadge,
  SectionHeader,
  StatCard,
} from '@milliswift/ui'
import { AlertTriangle, Database, HardDrive, Monitor, Rows3, Zap } from 'lucide-react'
import { useDb } from '../lib/db'

const formatRows = (rows: number): string => {
  if (rows >= 1e9) return `${(rows / 1e9).toFixed(1)}B`
  if (rows >= 1e6) return `${(rows / 1e6).toFixed(1)}M`
  if (rows >= 1e3) return `${(rows / 1e3).toFixed(1)}K`
  return String(rows)
}

/**
 * ⚠️  Latency figures on this page are preview data. There is no endpoint for
 * per-hour query latency yet (docs/PLATFORM_API_GAPS.md) — quota and usage
 * numbers are real.
 */
const LATENCY_SERIES = [
  { label: '00:00', p50: 12, p99: 87 },
  { label: '03:00', p50: 9, p99: 54 },
  { label: '06:00', p50: 11, p99: 71 },
  { label: '09:00', p50: 18, p99: 140 },
  { label: '12:00', p50: 14, p99: 92 },
  { label: '15:00', p50: 13, p99: 88 },
  { label: '18:00', p50: 15, p99: 103 },
  { label: '21:00', p50: 10, p99: 61 },
]

export default function Dashboard() {
  const { databaseId } = useDb()
  const quota = useQuery(tenantQueries.quota())
  const databases = useQuery(consoleQueries.databases())

  if (quota.isPending) return <PageSkeleton />

  const q = quota.data
  const db = databases.data?.find((d) => d.database_id === databaseId) ?? databases.data?.[0]

  return (
    <div className="space-y-8">
      <SectionHeader
        title="Dashboard"
        description={db ? `${db.name} · ${db.region}` : 'Overview of query activity and usage.'}
        action={<PreviewDataBadge />}
      />

      {q?.quota_exceeded_kind ? (
        <Callout tone="warning" title="Plan limit reached" icon={<AlertTriangle className="h-4 w-4" />}>
          {q.quota_exceeded_kind === 'rows'
            ? 'Queries are paused until the next period or an upgrade.'
            : 'Writes are paused until the next period or an upgrade.'}{' '}
          <a href="/usage" className="font-medium underline underline-offset-2">
            See usage
          </a>
        </Callout>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Queries this period" value={(q?.query_count ?? 0).toLocaleString()} icon={<Zap />} />
        <StatCard label="Rows scanned" value={formatRows(q?.rows_scanned ?? 0)} icon={<Rows3 />} />
        <StatCard
          label="Storage"
          value={`${(q?.gb_stored ?? 0).toFixed(1)} GB`}
          icon={<HardDrive />}
        />
        <StatCard label="Active devices" value={q ? '—' : '—'} icon={<Monitor />} hint="from the devices page" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard
          title="Response time"
          description="p50 and p99 across the last 24 hours · preview data"
        >
          <AreaTrendChart
            data={LATENCY_SERIES}
            series={[
              { key: 'p50', name: 'p50' },
              { key: 'p99', name: 'p99' },
            ]}
            valueSuffix="ms"
          />
        </ChartCard>

        <Card>
          <CardHeader>
            <div>
              <CardTitle>Quota</CardTitle>
              <p className="mt-0.5 text-caption text-muted">
                Since {q ? new Date(q.period_start).toLocaleDateString() : '—'}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => (window.location.href = '/settings')}>
              Plan settings
            </Button>
          </CardHeader>
          <CardBody className="space-y-5">
            <Meter
              value={q?.rows_percent_used ?? 0}
              max={100}
              label="Rows scanned"
              format={() => `${formatRows(q?.rows_scanned ?? 0)} · ${(q?.rows_percent_used ?? 0).toFixed(0)}%`}
            />
            <Meter
              value={q?.gb_written ?? 0}
              max={Math.max((q?.gb_written ?? 0) * 2, 1)}
              label="Written this period"
              format={(v) => `${v.toFixed(1)} GB`}
            />
            {db ? (
              <div className="flex items-center justify-between rounded-lg bg-sunken px-3 py-2.5 text-caption">
                <span className="flex items-center gap-2 text-muted">
                  <Database className="h-3.5 w-3.5" aria-hidden />
                  Active database
                </span>
                <span className="font-mono font-medium text-foreground">{db.name}</span>
              </div>
            ) : null}
          </CardBody>
        </Card>
      </div>
    </div>
  )
}
